import { io } from 'socket.io-client';
import { getCurrentEnvironment } from '../config/environments';

// Get the current environment configuration
let currentEnv = getCurrentEnvironment();
let SOCKET_URL = currentEnv.socketUrl;

class SocketService {
  constructor() {
    this.socket = null;
    this.connected = false;
    this.currentUrl = SOCKET_URL;
  }

  updateEnvironment(environment) {
    if (this.socket?.connected) {
      console.log('[Socket] Disconnecting from old environment');
      this.disconnect();
    }
    currentEnv = environment;
    SOCKET_URL = environment.socketUrl;
    this.currentUrl = SOCKET_URL;
    console.log('[Socket] Environment updated to:', environment.name, SOCKET_URL);
  }

  connect(token) {
    if (this.socket?.connected) {
      console.log('[Socket] Already connected');
      return;
    }

    console.log('[Socket] Connecting to', this.currentUrl);

    this.socket = io(this.currentUrl, {
      auth: { token },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      timeout: 10000, // 10 second connection timeout
    });

    this.socket.on('connect', () => {
      console.log('[Socket] Connected:', this.socket.id);
      this.connected = true;
    });

    this.socket.on('disconnect', (reason) => {
      console.log('[Socket] Disconnected:', reason);
      this.connected = false;
    });

    this.socket.on('connect_error', (error) => {
      console.error('[Socket] Connection error:', error.message);
    });

    this.socket.on('error', (error) => {
      console.error('[Socket] Error:', error);
    });
  }

  disconnect() {
    if (this.socket) {
      console.log('[Socket] Disconnecting');
      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
    }
  }

  // ============================================================================
  // Session Events
  // ============================================================================

  joinSession(sessionId) {
    if (!this.socket?.connected) {
      console.error('[Socket] Not connected');
      return;
    }

    console.log('[Socket] Joining session:', sessionId);
    this.socket.emit('join-session', { sessionId });
  }

  leaveSession(sessionId) {
    if (!this.socket?.connected) {
      return;
    }

    console.log('[Socket] Leaving session:', sessionId);
    this.socket.emit('leave-session', { sessionId });
  }

  onSessionJoined(callback) {
    if (!this.socket) return;
    this.socket.on('session-joined', callback);
  }

  onMemberJoined(callback) {
    if (!this.socket) return;
    this.socket.on('member-joined', callback);
  }

  onMemberLeft(callback) {
    if (!this.socket) return;
    this.socket.on('member-left', callback);
  }

  // ============================================================================
  // Screenshot Events
  // ============================================================================

  emitScreenshotCaptured(sessionId, screenshotId, position) {
    if (!this.socket?.connected) {
      console.error('[Socket] Not connected');
      return;
    }

    console.log('[Socket] Emitting screenshot captured:', screenshotId);
    this.socket.emit('screenshot-captured', {
      sessionId,
      screenshotId,
      position,
    });
  }

  onScreenshotUploaded(callback) {
    if (!this.socket) return;
    this.socket.on('screenshot-uploaded', callback);
  }

  onScreenshotConfirmed(callback) {
    if (!this.socket) return;
    this.socket.on('screenshot-confirmed', callback);
  }

  // ============================================================================
  // Position Updates
  // ============================================================================

  updatePosition(sessionId, position) {
    if (!this.socket?.connected) {
      return;
    }

    this.socket.emit('position-update', { sessionId, position });
  }

  onMemberPositionUpdate(callback) {
    if (!this.socket) return;
    this.socket.on('member-position-update', callback);
  }

  // ============================================================================
  // Utility
  // ============================================================================

  off(event, callback) {
    if (this.socket) {
      this.socket.off(event, callback);
    }
  }

  isConnected() {
    return this.connected && this.socket?.connected;
  }
}

// Export singleton instance
const socketService = new SocketService();
export default socketService;

